/**
 * Fenêtre des paramètres (ordinateur, propriétaire, 24/10/2026) : une grande
 * fenêtre par-dessus la page en cours, assombrie derrière, comme dans les
 * grandes applications. À gauche, la recherche puis les rubriques (son compte,
 * puis celles de « Mon entreprise » que son rôle permet de régler) ; à droite,
 * la page elle-même, la même que la page dédiée. Fermer (croix, Échap, clic sur
 * le fond) ramène à la page d'où l'on venait. Au téléphone, la fenêtre prend
 * tout l'écran ; on n'y arrive d'ailleurs que par le menu du compte de
 * l'ordinateur.
 */
import { Suspense, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { ArrowLeft, X, type LucideIcon } from 'lucide-react';
import { useAuth, useCan } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { ICONS } from '../../design/icons.js';
import { isSettingsPath, SETTINGS_HOME } from '../../app/settings-overlay.js';
import {
  ENTREPRISE_ANY, ENTREPRISE_GROUPS, groupTitle, sectionHint, sectionPath, sectionTitle, visibleSections,
} from '../../features/settings/sections/registry.js';
import { Overlay } from '../overlay.js';
import { Spinner } from '../ui.js';
import { SearchField } from '../ui/ListToolbar.js';

export interface NavEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Description et mots-clés, lus par la recherche (jamais affichés). */
  terms: string;
}

export interface NavGroup {
  title: string;
  entries: NavEntry[];
}

/** Mots-clés d'une rubrique pour la recherche ; aucun s'ils manquent au catalogue. */
function keywords(id: string): string {
  const key = `fenetreParametres.motsCles.${id}`;
  const text = t(key);
  return text === key ? '' : text;
}

/** Comparaison sans accents ni casse : « tva » trouve « TVA intracommunautaire ». */
function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * Rubriques qui contiennent tous les mots cherchés, dans leur nom, leur
 * description ou leurs mots-clés (« iban » mène à « Apparence des documents »).
 */
export function filterSettingsNav(groups: NavGroup[], query: string): NavGroup[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return groups;
  return groups
    .map((group) => ({
      ...group,
      entries: group.entries.filter((entry) => {
        const text = fold(`${entry.label} ${entry.terms}`);
        return words.every((word) => text.includes(word));
      }),
    }))
    .filter((group) => group.entries.length > 0);
}

/** Rubriques de la fenêtre : seulement celles qui s'ouvrent dans la fenêtre. */
function useSettingsNav(): NavGroup[] {
  const { me } = useAuth();
  const can = useCan();
  const groups: NavGroup[] = [{
    title: t('nav.monCompte'),
    entries: [
      { to: SETTINGS_HOME, label: t('nav.monCompte'), icon: ICONS.compte, terms: keywords('compte') },
      {
        to: '/profil/compte/notifications', label: t('compte.notifications.titre'), icon: ICONS.notifications,
        terms: `${t('compte.notifications.aide')} ${keywords('notifications')}`,
      },
    ],
  }];
  if (!ENTREPRISE_ANY.some((capability) => can(capability))) return groups;
  const sections = visibleSections(can, me?.enabled_modules ?? [])
    .filter((section) => isSettingsPath(sectionPath(section)));
  groups.push({
    title: t('nav.monEntreprise'),
    entries: [{
      to: '/entreprise', label: t('fenetreParametres.apercu'), icon: ICONS.entreprise,
      terms: `${t('nav.monEntreprise')} ${keywords('apercu')}`,
    }],
  });
  for (const group of ENTREPRISE_GROUPS) {
    const entries = sections
      .filter((section) => section.group === group)
      .map((section) => ({
        to: sectionPath(section), label: sectionTitle(section.id), icon: section.icon,
        terms: `${sectionHint(section.id)} ${keywords(section.id)}`,
      }));
    if (entries.length > 0) groups.push({ title: groupTitle(group), entries });
  }
  return groups;
}

/**
 * Pages ouvertes dans la fenêtre depuis son ouverture (clés d'historique) : la
 * flèche « Retour » n'apparaît qu'à partir de la deuxième, et le retour du
 * navigateur raccourcit la trace. Une redirection remplace la dernière.
 */
function useWindowTrail(): number {
  const location = useLocation();
  const type = useNavigationType();
  const [trail, setTrail] = useState<string[]>([location.key]);
  if (trail[trail.length - 1] === location.key) return trail.length;
  const index = trail.indexOf(location.key);
  const next = type === 'REPLACE'
    ? [...trail.slice(0, -1), location.key]
    : index >= 0 ? trail.slice(0, index + 1) : type === 'POP' ? [location.key] : [...trail, location.key];
  // État dérivé de l'adresse, ajusté pendant le rendu (motif React documenté).
  setTrail(next);
  return next.length;
}

export function SettingsDialog(props: { onClose: () => void; children: ReactNode }): ReactNode {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Dans une fenêtre, la page n'a plus de retour visible : la flèche le rend (24/10/2026).
  const canGoBack = useWindowTrail() > 1;
  const groups = useSettingsNav();
  const [query, setQuery] = useState('');
  const shown = filterSettingsNav(groups, query);
  // Entrée ouvre la première rubrique trouvée ; Échap vide d'abord la recherche
  // au lieu de fermer la fenêtre.
  const onSearchKey = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      const first = shown[0]?.entries[0];
      if (first && query.trim()) {
        event.preventDefault();
        navigate(first.to);
      }
    } else if (event.key === 'Escape' && query) {
      event.preventDefault();
      event.stopPropagation();
      setQuery('');
    }
  };
  // Une seule rubrique allumée : la plus précise (une fiche membre est sous « Membres »).
  const current = groups
    .flatMap((group) => group.entries)
    .filter((entry) => pathname === entry.to || pathname.startsWith(`${entry.to}/`))
    .sort((left, right) => right.to.length - left.to.length)[0];
  return (
    <Overlay label={t('nav.parametres')} onClose={props.onClose} variant="modale" size="fenetre">
      <div className="settings-window">
        <nav className="settings-window__nav" aria-label={t('fenetreParametres.rubriques')}>
          <h2 className="settings-window__title">{t('nav.parametres')}</h2>
          <div className="settings-window__search" role="search">
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder={t('fenetreParametres.recherche')}
              onKeyDown={onSearchKey}
            />
          </div>
          {shown.length === 0 ? (
            <p className="settings-window__empty" role="status">
              {t('fenetreParametres.aucunResultat', { terme: query.trim() })}
            </p>
          ) : null}
          {shown.map((group) => (
            <div key={group.title} className="settings-window__group" role="group" aria-label={group.title}>
              <span className="settings-window__group-title" aria-hidden="true">{group.title}</span>
              {group.entries.map((entry) => (
                <Link
                  key={entry.to}
                  to={entry.to}
                  className="settings-window__link"
                  aria-current={entry === current ? 'page' : undefined}
                >
                  <entry.icon size={18} aria-hidden="true" />
                  <span>{entry.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <div className="settings-window__content">
          <div className="settings-window__bar">
            {canGoBack ? (
              <button
                type="button"
                className="settings-window__tool"
                aria-label={t('fenetreParametres.retour')}
                title={t('fenetreParametres.retour')}
                onClick={() => navigate(-1)}
              >
                <ArrowLeft size={20} aria-hidden="true" />
              </button>
            ) : <span />}
            <button
              type="button"
              className="settings-window__tool"
              aria-label={t('fenetreParametres.fermer')}
              title={t('fenetreParametres.fermer')}
              onClick={props.onClose}
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <Suspense fallback={<Spinner />}>{props.children}</Suspense>
        </div>
      </div>
    </Overlay>
  );
}
